"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.findFormatter = exports.AuditDetailsDialog = void 0;
const DataTable_1 = require("@juvare/common-data-table-ui/DataTable");
const JuvareTheme_1 = require("@juvare/common-web-ui/JuvareTheme");
const utils_1 = require("@juvare/common-web-ui/utils");
const formatDateTime_1 = require("@juvare/common-web-ui/utils/formatDateTime");
const useAuditGet_1 = require("../../../../ui/hooks/useAuditGet");
const usePlatformSdkReactTranslation_1 = require("../../../../ui/hooks/usePlatformSdkReactTranslation");
const material_1 = require("@mui/material");
const react_1 = __importDefault(require("react"));
const diff_1 = require("../utils/diff");
const dot_1 = require("../utils/dot");
const AuditDetailsDialog = (props) => {
    const { t } = (0, usePlatformSdkReactTranslation_1.usePlatformSdkReactTranslation)();
    const { data: auditRecord, isLoading, isValidating, error } = (0, useAuditGet_1.useAuditGet)(props.id);
    const ariaLabelledById = `audit-dialog-title-${props.id}`;
    const ariaDescribedById = `audit-dialog-description-${props.id}`;
    let auditColumnValue;
    let Details;
    if (auditRecord) {
        const handlers = { ...defaultAuditHandlers, ...props.auditDetailsHandlers };
        const handler = handlers[auditRecord.eventType];
        const params = handler?.(auditRecord);
        if (params) {
            if (params.auditColumnValue) {
                auditColumnValue = String(params.auditColumnValue);
            }
            if (params.prevData) {
                Details = (react_1.default.createElement(FieldDiffDataTable, { t: t, data: params.data, entityAuditMapping: params.entityAuditMapping || {}, prevData: params.prevData, formatters: props.formatters, auditRecord: auditRecord }));
            }
            else {
                Details = (react_1.default.createElement(FieldDataTable, { t: t, data: params.data, entityAuditMapping: params.entityAuditMapping || {}, formatters: props.formatters, auditRecord: auditRecord }));
            }
        }
    }
    return (react_1.default.createElement(material_1.Dialog, { fullWidth: true, maxWidth: 'md', open: true, "aria-labelledby": ariaLabelledById, "aria-describedby": ariaDescribedById },
        react_1.default.createElement(material_1.DialogTitle, { id: ariaLabelledById }, props.dialogTitle ? props.dialogTitle : t('audit.dialogHeader')),
        react_1.default.createElement(material_1.DialogContent, { id: ariaDescribedById },
            auditRecord && error == null && (react_1.default.createElement(material_1.Box, { sx: { backgroundColor: JuvareTheme_1.themeColors.surface.secondary, m: 0, p: 2, fontSize: 12 } },
                react_1.default.createElement(material_1.Grid, { container: true, spacing: 2 },
                    react_1.default.createElement(GridItem, { label: t('audit.dateTime'), value: (0, formatDateTime_1.formatDateTime)(new Date(auditRecord.timestamp)) }),
                    react_1.default.createElement(GridItem, { label: t('audit.userName'), value: auditRecord.userName }),
                    auditRecord.originUserName && (react_1.default.createElement(GridItem, { label: t('audit.originUserName'), value: auditRecord.originUserName })),
                    auditRecord.entityName && react_1.default.createElement(GridItem, { label: t('audit.entity'), value: auditRecord.entityName }),
                    auditColumnValue && react_1.default.createElement(GridItem, { label: t('audit.auditColumnValue'), value: auditColumnValue }),
                    react_1.default.createElement(GridItem, { label: t('audit.eventType'), value: auditRecord.eventType })))),
            !isLoading && !isValidating && error != null && react_1.default.createElement(material_1.Alert, { severity: 'error' }, (0, utils_1.getErrorMessage)(error)),
            Details && react_1.default.createElement(material_1.Box, { pt: 2 }, Details)),
        react_1.default.createElement(material_1.DialogActions, null,
            react_1.default.createElement(material_1.Button, { onClick: () => props.onClose() }, t('common.ok')))));
};
exports.AuditDetailsDialog = AuditDetailsDialog;
const defaultAuditHandlers = {
    create: (auditRecord) => ({
        data: auditRecord.params?.data,
        entityAuditMapping: auditRecord.params?.entityAuditMapping,
    }),
    update: (auditRecord) => ({
        data: auditRecord.params?.data,
        prevData: auditRecord.params?.prevData ?? {},
        entityAuditMapping: auditRecord.params?.entityAuditMapping,
        auditColumnValue: auditRecord.params?.auditColumnValue,
    }),
    delete: (auditRecord) => ({
        data: {},
        prevData: auditRecord.params?.prevData ?? {},
        entityAuditMapping: auditRecord.params?.entityAuditMapping,
    }),
    get: (auditRecord) => ({
        data: { id: auditRecord.entityId ?? '' },
        auditColumnValue: auditRecord.params?.auditColumnValue,
    }),
    list: (auditRecord) => ({
        data: auditRecord.params?.ids,
        entityAuditMapping: auditRecord.params?.entityAuditMapping,
    }),
    'mass-create': (auditRecord) => ({
        data: auditRecord.params?.ids,
        entityAuditMapping: auditRecord.params?.entityAuditMapping,
    }),
    'mass-update': (auditRecord) => ({
        data: auditRecord.params?.ids,
        entityAuditMapping: auditRecord.params?.entityAuditMapping,
    }),
    'mass-delete': (auditRecord) => ({
        data: auditRecord.params?.ids,
        entityAuditMapping: auditRecord.params?.entityAuditMapping,
    }),
};
const defaultFormatters = {
    '.*timestamp.*|.*createdAt|.*updatedAt': (_key, oldValue, newValue) => {
        const result = {};
        if (oldValue && !isNaN(oldValue)) {
            result.oldValue = (0, formatDateTime_1.formatDateTime)(new Date(oldValue));
        }
        if (newValue && !isNaN(newValue)) {
            result.newValue = (0, formatDateTime_1.formatDateTime)(new Date(newValue));
        }
        return result;
    },
};
const FieldDiffDataTable = ({ t, formatters = {}, auditRecord, prevData, data, entityAuditMapping }) => {
    const columns = [
        {
            cellField: 'id',
            headerLabel: t('audit.field'),
            showToolbarWhenTextOverflows: true,
        },
        {
            cellField: 'oldValue',
            headerLabel: t('audit.oldValue'),
            showToolbarWhenTextOverflows: true,
        },
        {
            cellField: 'newValue',
            headerLabel: t('audit.newValue'),
            showToolbarWhenTextOverflows: true,
        },
    ];
    const objectDiff = (0, diff_1.diff)((0, dot_1.dot)(prevData ?? {}), (0, dot_1.dot)(data ?? {}));
    const rows = Object.entries(objectDiff).map(([key, { oldValue, newValue }]) => {
        const formatter = (0, exports.findFormatter)(key, { ...formatters, ...defaultFormatters });
        const result = (formatter &&
            formatter(key, entityAuditMapping[oldValue] ?? oldValue, entityAuditMapping[newValue] ?? newValue, auditRecord)) ||
            undefined;
        return {
            id: (result?.key ?? formatAuditFieldName(key, entityAuditMapping)),
            oldValue: (result?.oldValue ?? entityAuditMapping[oldValue] ?? oldValue),
            newValue: (result?.newValue ?? entityAuditMapping[newValue] ?? newValue),
        };
    });
    return react_1.default.createElement(DataTable_1.DataTable, { columns: columns, loading: false, rows: rows, tableSx: { tableLayout: 'fixed' } });
};
const findFormatter = (key, formatters) => {
    const formatterKey = Object.keys(formatters).find((formatterKey) => new RegExp(formatterKey, 'i').test(key));
    return formatterKey ? formatters[formatterKey] : undefined;
};
exports.findFormatter = findFormatter;
function formatAuditFieldName(fieldName, entityAuditMapping) {
    if (entityAuditMapping) {
        (fieldName.match(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi) ?? [])
            .filter((uuid) => entityAuditMapping[uuid])
            .forEach((uuid) => {
            fieldName = fieldName.replace(uuid, entityAuditMapping[uuid]);
        });
    }
    return fieldName;
}
const FieldDataTable = ({ t, formatters, auditRecord, data, entityAuditMapping }) => {
    const columns = [
        {
            cellField: 'id',
            headerLabel: t('audit.field'),
        },
        {
            cellField: 'value',
            headerLabel: t('audit.value'),
        },
    ];
    const rows = Object.entries((0, dot_1.dot)(data ?? {})).map(([key, value]) => {
        const formatter = (0, exports.findFormatter)(key, { ...formatters, ...defaultFormatters });
        const val = entityAuditMapping?.[value] ?? value;
        const result = (formatter && formatter(key, val, val, auditRecord)) || undefined;
        return {
            id: result?.key || key,
            value: (result?.oldValue ?? val),
        };
    });
    return react_1.default.createElement(DataTable_1.DataTable, { columns: columns, loading: false, rows: rows, tableSx: { tableLayout: 'fixed' } });
};
const GridItem = ({ label, value }) => {
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(material_1.Grid, { size: 4 },
            react_1.default.createElement(material_1.Typography, { variant: 'body2', color: 'textSecondary' }, label)),
        react_1.default.createElement(material_1.Grid, { size: 8 },
            react_1.default.createElement(material_1.Typography, { variant: 'body2' }, value))));
};
